
import { universalStorage } from '../../storage/universalStorage';
import { mediaStorage, MediaFile } from '../../storage/mediaStorage';
import { logger } from '@/utils/logging';
import { v4 as uuidv4 } from 'uuid';

export interface Report {
  id: string;
  title: string;
  type: 'student' | 'teacher' | 'admin' | 'financial' | 'attendance' | 'performance';
  description?: string;
  generatedBy: string;
  generatedAt: string;
  startDate: string;
  endDate: string;
  data: any;
  format: 'json' | 'csv' | 'pdf' | 'excel';
  attachments?: string[];
  metadata?: {
    totalRecords?: number;
    filters?: any;
    summary?: any;
  };
  createdAt: string;
  updatedAt: string;
}

class ReportsFileStorageService {
  private readonly STORAGE_KEY = 'nalc_reports';
  private reportsCache: Report[] | null = null;
  
  async getAllReports(): Promise<Report[]> {
    try {
      console.log('📊 Loading reports from localStorage only (enhanced 5000+ capacity)...');
      
      // Return cached reports if available
      if (this.reportsCache) {
        return this.reportsCache;
      }
      
      const reports = await universalStorage.getItem<Report[]>(this.STORAGE_KEY);
      const result = reports || [];
      
      // Cache the result
      this.reportsCache = result;
      
      console.log(`✅ Loaded ${result.length} reports from localStorage`);
      return result;
    } catch (error) {
      logger.error('Failed to get all reports', { component: 'ReportsFileStorage' }, error as Error);
      this.reportsCache = [];
      return [];
    }
  }

  async getReportById(id: string): Promise<Report | undefined> {
    try {
      const reports = await this.getAllReports();
      return reports.find(report => report.id === id);
    } catch (error) {
      logger.error(`Failed to get report ${id}`, { component: 'ReportsFileStorage' }, error as Error);
      return undefined;
    }
  }

  async addReport(report: Omit<Report, 'id' | 'createdAt' | 'updatedAt'>): Promise<Report> {
    try {
      const reports = await this.getAllReports();
      
      const newReport: Report = {
        ...report,
        id: uuidv4(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      reports.push(newReport);
      await universalStorage.setItem(this.STORAGE_KEY, reports);
      
      // Update cache
      this.reportsCache = reports;
      
      // Save individual report file for enhanced persistence
      if (universalStorage.isDesktopStorageActive()) {
        await universalStorage.setItem(`nalc_reports:${newReport.id}`, newReport);
      }
      
      logger.info('Report added successfully', { 
        component: 'ReportsFileStorage',
        data: { title: newReport.title, id: newReport.id, type: newReport.type }
      });
      
      return newReport;
    } catch (error) {
      logger.error('Failed to add report', { component: 'ReportsFileStorage' }, error as Error);
      throw error;
    }
  }

  async updateReport(id: string, report: Partial<Omit<Report, 'id' | 'createdAt'>>): Promise<Report> {
    try {
      const reports = await this.getAllReports();
      const index = reports.findIndex(r => r.id === id);
      
      if (index === -1) {
        throw new Error(`Report with ID ${id} not found`);
      }
      
      const updatedReport = {
        ...reports[index],
        ...report,
        updatedAt: new Date().toISOString(),
      };
      
      reports[index] = updatedReport;
      await universalStorage.setItem(this.STORAGE_KEY, reports);
      
      // Update cache
      this.reportsCache = reports;
      
      // Update individual report file for enhanced persistence
      if (universalStorage.isDesktopStorageActive()) {
        await universalStorage.setItem(`nalc_reports:${id}`, updatedReport);
      }
      
      logger.info('Report updated successfully', { 
        component: 'ReportsFileStorage',
        data: { title: updatedReport.title, id: updatedReport.id }
      });
      
      return updatedReport;
    } catch (error) {
      logger.error(`Failed to update report ${id}`, { component: 'ReportsFileStorage' }, error as Error);
      throw error;
    }
  }

  async deleteReport(id: string): Promise<void> {
    try {
      const reports = await this.getAllReports();
      const filteredReports = reports.filter(r => r.id !== id);
      
      await universalStorage.setItem(this.STORAGE_KEY, filteredReports);
      
      // Update cache
      this.reportsCache = filteredReports;
      
      // Remove individual report file for enhanced persistence
      if (universalStorage.isDesktopStorageActive()) {
        await universalStorage.removeItem(`nalc_reports:${id}`);
      }
      
      logger.info('Report deleted successfully', { 
        component: 'ReportsFileStorage',
        data: { id }
      });
    } catch (error) {
      logger.error(`Failed to delete report ${id}`, { component: 'ReportsFileStorage' }, error as Error);
      throw error;
    }
  }

  async saveReportAttachment(reportId: string, attachmentFile: File): Promise<string> {
    try {
      const report = await this.getReportById(reportId);
      if (!report) {
        throw new Error(`Report with ID ${reportId} not found`);
      }

      const base64String = await this.fileToBase64(attachmentFile);
      
      const mediaFile: MediaFile = {
        id: uuidv4(),
        name: attachmentFile.name,
        type: attachmentFile.type,
        size: attachmentFile.size,
        data: base64String,
        section: 'reports',
        category: 'documents',
        uploadDate: new Date().toISOString(),
        metadata: {
          originalName: attachmentFile.name,
          description: `Attachment for report ${report.title}`
        }
      };

      await mediaStorage.saveMedia(mediaFile);
      
      // Update report with attachment reference
      const updatedAttachments = [...(report.attachments || []), mediaFile.id];
      await this.updateReport(reportId, { attachments: updatedAttachments });
      
      return mediaFile.id;
    } catch (error) {
      logger.error('Failed to save report attachment', { 
        component: 'ReportsFileStorage',
        data: { reportId }
      }, error as Error);
      throw error;
    }
  }

  // Clear cache method
  clearCache(): void {
    this.reportsCache = null;
  }

  // Reset to defaults
  async resetReports(): Promise<void> {
    try {
      console.log('📊 Resetting reports to defaults...');
      await universalStorage.setItem(this.STORAGE_KEY, []);
      this.clearCache();
      console.log('✅ Reports reset to defaults');
    } catch (error) {
      logger.error('Error resetting reports', { component: 'ReportsFileStorage' }, error as Error);
      throw error;
    }
  }

  // Export reports
  async exportReports(): Promise<string> {
    const reports = await this.getAllReports();
    return JSON.stringify(reports, null, 2);
  }

  // Import reports
  async importReports(reportsJson: string): Promise<void> {
    try {
      const reports = JSON.parse(reportsJson) as Report[];
      await universalStorage.setItem(this.STORAGE_KEY, reports);
      this.clearCache();
      console.log('✅ Reports imported successfully');
    } catch (error) {
      logger.error('Error importing reports', { component: 'ReportsFileStorage' }, error as Error);
      throw new Error('Invalid reports format');
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}

export const reportsFileStorageService = new ReportsFileStorageService();
